import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Plus, Calendar, Users, FileText, Edit, Eye, Loader2, LogOut, CheckSquare, ListTodo, Lock, Shield, Mail, Settings, Share2 } from 'lucide-react';
import { pvService, memberService, clubService, clubInfoService, handleApiError } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import ClubLogo from './ClubLogo';
import SharePVModal from './SharePVModal';

const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, logout } = useAuth();
  const [pvList, setPvList] = useState([]);
  const [activeMembers, setActiveMembers] = useState([]);
  const [clubStats, setClubStats] = useState(null);
  const [clubInfo, setClubInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingEmails, setSendingEmails] = useState({}); // Track sending state per PV
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedPvForShare, setSelectedPvForShare] = useState(null);

  // Handle sending PV by email
  const handleSendEmail = async (pvId) => {
    try {
      setSendingEmails(prev => ({ ...prev, [pvId]: true }));
      const result = await pvService.sendEmail(pvId);
      
      toast({
        title: "Email envoyé",
        description: result.message,
        variant: "default",
      });
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur",
        description: `Impossible d'envoyer l'email: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setSendingEmails(prev => ({ ...prev, [pvId]: false }));
    }
  };


  // Handle opening share modal
  const handleOpenShareModal = (pv) => {
    setSelectedPvForShare(pv);
    setShareModalOpen(true);
  };

  // Handle closing share modal
  const handleCloseShareModal = () => {
    setShareModalOpen(false);
    setSelectedPvForShare(null);
  };

  // Handle share generated
  const handleShareGenerated = (pvId, shareToken) => {
    // Update PV list with new share token
    setPvList(prev => prev.map(pv => 
      pv.id === pvId 
        ? { ...pv, share_enabled: true, share_token: shareToken }
        : pv
    ));
    
    toast({
      title: "Lien généré",
      description: "Le lien de partage a été créé avec succès",
    });
  };

  // Handle share revoked
  const handleShareRevoked = (pvId) => {
    // Update PV list
    setPvList(prev => prev.map(pv => 
      pv.id === pvId 
        ? { ...pv, share_enabled: false }
        : pv
    ));
    
    toast({
      title: "Lien révoqué",
      description: "Le lien de partage a été révoqué",
    });
  };


  // Load PVs and members from API
  const loadData = async () => {
    try {
      setLoading(true);
      const [pvs, members, stats, info] = await Promise.all([
        pvService.getAll(),
        memberService.getAll(),
        clubService.getStatistics(),
        clubInfoService.get().catch(() => null) // Don't fail if club info is not available
      ]);
      setPvList(pvs);
      // Filter to show only active members
      setActiveMembers(members.filter(m => m.active));
      setClubStats(stats);
      setClubInfo(info);
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast({
        title: "Erreur",
        description: `Impossible de charger les données: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Écouter les mises à jour des PV
    const handlePvUpdate = () => {
      loadData();
    };

    window.addEventListener('pvUpdated', handlePvUpdate);
    
    return () => {
      window.removeEventListener('pvUpdated', handlePvUpdate);
    };
  }, []);

  const formatCommitteeDate = (dateString) => {
    if (!dateString) return 'Aucune date';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const countPointsOrdreJour = (pv) => {
    // Support both snake_case (old) and camelCase (new) field names
    const points = pv.pointsOrdreJour || pv.points_ordre_jour;
    if (!points) return 0;
    
    return points.filter(point => {
      if (typeof point === 'string') {
        return point.trim().length > 0;
      } else if (typeof point === 'object' && point !== null) {
        return point.titre && point.titre.trim().length > 0;
      }
      return false;
    }).length;
  };

  const countTachesEnCours = (pv) => {
    // Support both snake_case (old) and camelCase (new) field names
    const taches = pv.prochainEtapes || pv.prochaines_etapes;
    if (!taches) return 0;
    
    return taches.filter(tache => 
      (tache.statut === 'En cours' || tache.statut === 'En suspens') && 
      tache.tache && tache.tache.trim().length > 0
    ).length;
  };

  // Get status badge configuration
  const getStatusBadge = (status) => {
    switch (status) {
      case 'validated':
        return { 
          label: 'Validé', 
          className: 'bg-blue-100 text-blue-700 text-xs px-2 py-1',
          icon: <Shield className="w-3 h-3 mr-1" />
        };
      case 'locked':
        return { 
          label: 'Verrouillé', 
          className: 'bg-red-100 text-red-700 text-xs px-2 py-1',
          icon: <Lock className="w-3 h-3 mr-1" />
        };
      default:
        return { 
          label: 'Brouillon', 
          className: 'bg-gray-100 text-gray-600 text-xs px-2 py-1',
          icon: <Edit className="w-3 h-3 mr-1" />
        };
    }
  };

  const handleLogout = () => {
    logout();
    toast({
      title: "Déconnexion",
      description: "Vous avez été déconnecté avec succès.",
    });
    navigate('/');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-green-600" />
            <p className="text-gray-600">Chargement des PV...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header with club info and logout */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          {/* Logo du club (pour tous les clubs) */}
          <ClubLogo />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              {clubInfo?.display_name || clubInfo?.name || user?.name || 'Dashboard'}
            </h1>
            <p className="text-gray-600">
              Gestion des procès-verbaux • Connecté en tant que <strong>{user?.username}</strong>
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => navigate('/club-settings')}
            className="border-gray-200 text-gray-600 hover:bg-gray-50"
          >
            <Settings className="w-4 h-4 mr-2" />
            Paramètres
          </Button>
          <Button 
            variant="outline"
            onClick={handleLogout}
            className="border-red-200 text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="border-orange-200 bg-orange-50 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center">
              <FileText className="w-10 h-10 text-orange-600 mr-4" />
              <div>
                <p className="text-2xl font-bold text-orange-700">{pvList.length}</p>
                <p className="text-orange-600">PV totaux</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-blue-200 bg-blue-50 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center">
              <Calendar className="w-10 h-10 text-blue-600 mr-4" />
              <div>
                <p className="text-2xl font-bold text-blue-700">
                  {pvList.length > 0 && pvList[0].prochainComite 
                    ? formatCommitteeDate(pvList[0].prochainComite) 
                    : formatCommitteeDate(clubStats?.latest_committee_date)}
                </p>
                <p className="text-blue-600">Date du prochain comité</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="border-purple-200 bg-purple-50 shadow-md cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/members')}
        >
          <CardContent className="p-6">
            <div className="flex items-center">
              <Users className="w-10 h-10 text-purple-600 mr-4" />
              <div>
                <p className="text-2xl font-bold text-purple-700">{activeMembers.length}</p>
                <p className="text-purple-600">Membres du comité</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="mb-8 flex flex-wrap gap-4">
        <Button 
          onClick={() => navigate('/pv/new')}
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 text-lg"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nouveau PV
        </Button>
        
        <Button 
          onClick={() => navigate('/propositions-management')}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg"
        >
          <Mail className="w-5 h-5 mr-2" />
          Collecter des Propositions
        </Button>
      </div>

      {/* PV List */}
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">
          Procès-verbaux récents
        </h2>
        
        {pvList.length === 0 ? (
          <Card className="border-gray-200 shadow-md">
            <CardContent className="p-8 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                Aucun PV créé
              </h3>
              <p className="text-gray-500 mb-6">
                Commencez par créer votre premier procès-verbal
              </p>
              <Button 
                onClick={() => navigate('/pv/new')}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Créer le premier PV
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {pvList.map((pv) => (
              <Card key={pv.id} className="border-green-200 bg-white shadow-md hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                      <CardTitle className="text-green-700 mb-2">
                        PV du {formatDate(pv.date)}
                      </CardTitle>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-gray-600 mb-3">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1 flex-shrink-0" />
                          <span className="whitespace-nowrap">{pv.time}</span>
                        </div>
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-1 flex-shrink-0" />
                          <span className="whitespace-nowrap">{pv.presents.length} présents</span>
                        </div>
                        <div className="flex items-center">
                          <CheckSquare className="w-4 h-4 mr-1 flex-shrink-0" />
                          <span className="whitespace-nowrap">{countPointsOrdreJour(pv)} points</span>
                        </div>
                        <div className="flex items-center">
                          <ListTodo className="w-4 h-4 mr-1 flex-shrink-0" />
                          <span className="whitespace-nowrap">{countTachesEnCours(pv)} tâches</span>
                        </div>
                        <Badge className={getStatusBadge(pv.status).className}>
                          {getStatusBadge(pv.status).icon}
                          {getStatusBadge(pv.status).label}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/pv/${pv.id}`)}
                        className="border-blue-200 text-blue-600 hover:bg-blue-50 flex-1 min-w-0 sm:flex-initial"
                      >
                        <Eye className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Voir</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/pv/${pv.id}/edit`)}
                        disabled={pv.status === 'locked'}
                        className="border-green-200 text-green-600 hover:bg-green-50 flex-1 min-w-0 sm:flex-initial disabled:opacity-50 disabled:cursor-not-allowed"
                        title={pv.status === 'locked' ? 'PV verrouillé - modification impossible' : ''}
                      >
                        <Edit className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Modifier</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendEmail(pv.id)}
                        disabled={sendingEmails[pv.id]}
                        className="border-orange-200 text-orange-600 hover:bg-orange-50 flex-1 min-w-0 sm:flex-initial disabled:opacity-50"
                        title="Envoyer le PV par email aux membres actifs"
                      >
                        {sendingEmails[pv.id] ? (
                          <Loader2 className="w-4 h-4 animate-spin sm:mr-2" />
                        ) : (
                          <Mail className="w-4 h-4 sm:mr-2" />
                        )}
                        <span className="hidden sm:inline">
                          {sendingEmails[pv.id] ? 'Envoi...' : 'Email'}
                        </span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenShareModal(pv)}
                        className="border-purple-200 text-purple-600 hover:bg-purple-50 flex-1 min-w-0 sm:flex-initial"
                        title="Partager le PV en lecture seule"
                      >
                        <Share2 className="w-4 h-4 sm:mr-2" />
                        <span className="hidden sm:inline">Partager</span>
                        {pv.share_enabled && (
                          <span className="ml-1 inline-flex h-2 w-2 rounded-full bg-purple-600"></span>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
    
    {/* Share PV Modal */}
    {selectedPvForShare && (
      <SharePVModal
        isOpen={shareModalOpen}
        onClose={handleCloseShareModal}
        pv={selectedPvForShare}
        onShareGenerated={handleShareGenerated}
        onShareRevoked={handleShareRevoked}
      />
    )}
    </div>
  );
};

export default Dashboard;